import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { useKeycloak } from '../components/KeycloakProvider';
import { clearAuthCookies } from '@shared/services/authApi';
import { logoutToLogoutPage } from '../services/keycloakService';
import * as authService from '../services/authService';

/**
 * Hook providing authentication actions and state

 * @returns {{ 
 *   user, 
 *   isAuthenticated, 
 *   isLoading, 
 *   error, 
 *   login, 
 *   logout,
 *   loginWithCompanySelection 
 * }}
 */
export function useAuth() {
  const { user, isAuthenticated, isLoading, error, selectedCompany, setAuth, logout: storeLogout, setLoading, setError } = useAuthStore();
  const navigate = useNavigate();
 const { keycloak } = useKeycloak();
  const login = useCallback(
    async (email, password) => {
      setLoading(true);
      setError(null);
      try {
        const { user: authUser, token } = await authService.login(email, password);
        setAuth(authUser, token);
        navigate('/dashboard');
      } catch (err) {
        const message = err.response?.data?.message || 'Login failed. Please try again.';
        setError(message);
      } finally {
        setLoading(false);
      }
    },
    [setAuth, setError, setLoading, navigate]
  );

  /**
   * Login with company selection flow
   * Returns a callback that should be called after company is selected to navigate to dashboard
   */
  const loginWithCompanySelection = useCallback(
    async (email, password) => {
      setLoading(true);
      setError(null);
      try {
        const { user: authUser, token } = await authService.login(email, password);
        setAuth(authUser, token);
        // Return success - caller will handle company selection and navigation
        return { success: true };
      } catch (err) {
        const message = err.response?.data?.message || 'Login failed. Please try again.';
        setError(message);
        return { success: false, error: message };
      } finally {
        setLoading(false);
      }
    },
    [setAuth, setError, setLoading]
  );

  /**
   * Log out of the portal. By default only the portal's session ends and NumberCruncher Web stays
   * signed in; `{ global: true }` ends the Keycloak session for every application (used after
   * account deletion and a password change). See logoutToLogoutPage.
   * @param {{ global?: boolean }} [options]
   */
  const logout = useCallback(async ({ global = false } = {}) => {
    setLoading(true);
    try {
      // Step 1: Clear active firm cookie
      await authService.clearActiveFirm();
    } catch (error) {
      console.warn('[Logout] Failed to clear active firm cookie:', error);
    }

    try {
      // Step 2: Clear backend auth cookies
      await clearAuthCookies();
    } catch (error) {
      console.warn('[Logout] Failed to clear backend cookies:', error);
    } finally {
      // Step 3: Clear session storage (for company selection flow)
      sessionStorage.removeItem('keycloak_session_active');

      // Step 4: Clear local state
      storeLogout();
      setLoading(false);
      
      // Step 5: End the Keycloak side and land on the public /logout page, so the user gets an
      // explicit "signed out" confirmation with a Re Login button instead of being bounced straight
      // back to sign-in. Shared with the forced-logout path — see logoutToLogoutPage.
      await logoutToLogoutPage(keycloak, { global });
    }
    // `navigate` is intentionally absent: this callback redirects via Keycloak / window.location,
    // never through the router.
  }, [keycloak, storeLogout, setLoading]);

  return { 
    user, 
    isAuthenticated, 
    isLoading, 
    error, 
    selectedCompany,
    login, 
    logout,
    loginWithCompanySelection 
  };
}
